import { useEffect } from 'react';
import { SITE, canonicalUrl } from '../lib/site';

interface SeoProps {
  title: string;
  description?: string;
  path?: string;
  type?: 'website' | 'article';
  /** Optional image URL (absolute) for OG / Twitter cards. Defaults to the brand logo. */
  image?: string;
  /** Structured data to inject as JSON-LD for this route (object or array). */
  jsonLd?: object | object[];
}

const DEFAULT_IMAGE = `${SITE.url}/og.png`;
const JSONLD_ATTR = 'data-seo-jsonld';

/**
 * Per-route SEO manager. Mutates document.head at runtime:
 * - title, description, canonical, Open Graph, Twitter
 * - per-route JSON-LD structured data (WebPage + BreadcrumbList + any provided)
 *
 * Baseline Organization / WebSite / Service schema lives in public/index.html.
 */
export function Seo({
  title,
  description,
  path = '',
  type = 'website',
  image,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const desc = description || SITE.description;
    const url = canonicalUrl(path);
    const img = image || DEFAULT_IMAGE;

    document.title = title;

    setMeta('description', desc);
    setMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1');

    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:url', url, true);
    setMeta('og:type', type, true);
    setMeta('og:site_name', SITE.name, true);
    setMeta('og:image', img, true);
    setMeta('og:image:alt', `${SITE.name} — ${SITE.tagline}`, true);
    setMeta('og:locale', 'en_US', true);

    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', img);

    setLink('canonical', url);

    // Build route-level structured data.
    const segments = path.split('/').filter(Boolean);
    const breadcrumbs = buildBreadcrumbs(segments);
    const webPage = {
      '@context': 'https://schema.org',
      '@type': type === 'article' ? 'Article' : 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description: desc,
      isPartOf: { '@type': 'WebSite', name: SITE.name, url: SITE.url },
      primaryImageOfPage: { '@type': 'ImageObject', url: img },
      inLanguage: 'en',
    };

    const blocks: object[] = [webPage, breadcrumbs];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) blocks.push(...jsonLd);
      else blocks.push(jsonLd);
    }

    injectJsonLd(blocks);
  }, [title, description, path, type, image, jsonLd]);

  // Clean up injected JSON-LD on unmount.
  useEffect(() => () => purgeJsonLd(), []);

  return null;
}

function buildBreadcrumbs(segments: string[]) {
  const items: object[] = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE.url },
  ];
  let acc = '';
  segments.forEach((seg, i) => {
    acc += `/${seg}`;
    const name = prettify(seg);
    items.push({
      '@type': 'ListItem',
      position: i + 2,
      name,
      item: canonicalUrl(acc),
    });
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function prettify(segment: string): string {
  if (segment.includes('-to-')) {
    const [from, to] = segment.split('-to-');
    return `${from.split('_')[0].toUpperCase()} to ${to.split('_')[0].toUpperCase()} Exchange`;
  }
  return segment
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

function injectJsonLd(blocks: object[]) {
  purgeJsonLd();
  for (const block of blocks) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(JSONLD_ATTR, 'true');
    script.text = JSON.stringify(block);
    document.head.appendChild(script);
  }
}

function purgeJsonLd() {
  document.head
    .querySelectorAll(`script[${JSONLD_ATTR}]`)
    .forEach((el) => el.remove());
}

function setMeta(name: string, content: string, openGraph = false) {
  const attr = openGraph ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
